import { GeoHash } from 'geohash';
import * as queryString from 'query-string';
import { useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import ButtonBase from '../../components/button/ButtonBase';
import ButtonText from '../../components/button/button-text/ButtonText';
import LoaderBar from '../../components/loader-bar/LoaderBar';
import Spinner from '../../components/spinner/Spinner';
import VisibilitySensor from '../../components/visibility-sensor/VisibilitySensor';
import NoHits from './NoHits';
import { fetchPopulation, performSearch, searchForArea } from './project-tva-bosse-actions';
import AreaInfo from './area-info/AreaInfo';
import { TvaProjectCards } from './TvaProjectCards';
import AreaDropdown from './area-dropdown/AreaDropdown';
import MaxWidthWrapper from '../../components/max-width-wrapper/MaxWidthWrapper';
import ProjectCardContext, {
    CARD_FOOTER_LOCATION_INFORMATION_OPTIONS,
    CARD_FOOTER_OPTIONS,
} from '../../context/project-card-context';
import { clearProjectCardsState } from '../project-actions';
import { fetchAllCounties, fetchAllMunicipalities, fetchAllSuburbs } from '../../search/search-actions';
import RoundedButtonPrimary from '../../components/button/rounded-button-primary/RoundedButtonPrimary';
import { InputTextLike } from '@sb1/ffe-form-react';
import styles from './project-tva-bosse-samleside.module.less';

const TvaFindBosse = ({
    tvaEventId,
    p3aEventId,
    baseUrl,
    queryMunicipality,
    queryCounty,
    querySuburb,
    cmsSelectedProjects,
    max = 50,
}) => {
    const dispatch = useDispatch();
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);

    const [allMunicipalities, setAllMunicipalities] = useState([]);
    const [allSuburbs, setAllSuburbs] = useState([]);
    const [allCounties, setAllCounties] = useState([]);
    const [focused, setFocused] = useState(false);
    const [position, setPosition] = useState(null);
    const [fetchingLocation, setFetchingLocation] = useState(false);
    const [currentSuburb, setCurrentSuburb] = useState(null);
    const [currentMunicipality, setCurrentMunicipality] = useState(null);
    const [currentCounty, setCurrentCounty] = useState(null);
    const [population, setPopulation] = useState({});
    const [fetchMore, setFetchMore] = useState(false);

    const [total, setTotal] = useState(0);
    const [from, setFrom] = useState(0);

    const searchField = useRef();
    const navigate = useNavigate();
    const location = useLocation();

    const queryStringParsed = queryString.parse(location.search);
    const urlQueryParameter = queryStringParsed?.query;
    const queryHash = queryStringParsed?.hash;
    const replacedLocation = queryStringParsed?.rl;

    const updateUrl = ({
        query = urlQueryParameter,
        county = queryCounty,
        municipality = queryMunicipality,
        hash = queryHash,
        suburb = querySuburb,
        rl = '',
    }) => {
        const searchString = queryString.stringify({ query, hash, rl });
        if (county === 'Oslo' || municipality === 'Oslo') {
            county = 'Oslo';
            municipality = 'Oslo';
        } else if (county === 'Svalbard' || municipality === 'Svalbard') {
            county = 'Svalbard';
            municipality = 'Svalbard';
        } else if (county === 'Jan Mayen' || municipality === 'Jan Mayen') {
            county = 'Jan Mayen';
            municipality = 'Jan Mayen';
        }
        navigate(
            {
                pathname: `/bosser${county ? '/' + county : ''}${municipality ? '/' + municipality : ''}${
                    suburb ? '/' + suburb : ''
                }`,
                search: query || hash || rl ? searchString : null,
            },
            { replace: true, preventScrollReset: true }
        );
    };

    const searchSubmit = (e) => {
        searchField.current.blur();
        e.preventDefault();
        const searchString = searchField.current.value;
        if (!searchString || searchString?.length === 0) {
            return false;
        }
        searchForArea({ baseUrl, name: searchString })
            .then((location) => {
                if (!location) {
                    updateUrl({ query: searchString });
                } else {
                    updateUrl({
                        county: location.county,
                        municipality: location.municipality || '',
                        suburb: location.suburb || '',
                        query: '',
                        hash: '',
                        rl: searchString,
                    });
                }
            })
            .catch(() => updateUrl({ query: searchString }));

        return false;
    };

    useEffect(() => {
        setFrom(0);
    }, [urlQueryParameter, queryCounty, queryMunicipality, querySuburb, queryHash]);

    useEffect(() => {
        searchField.current.value = urlQueryParameter || '';
        if (
            urlQueryParameter ||
            queryCounty ||
            queryMunicipality ||
            querySuburb ||
            queryHash ||
            cmsSelectedProjects.length === 0
        ) {
            search({
                query: urlQueryParameter,
                county: queryCounty,
                municipality: queryMunicipality,
                suburb: querySuburb,
                geohash: queryHash,
                from,
            });
        } else {
            setResults([]);
            setTotal(0);
            setFrom(0);
        }
    }, [urlQueryParameter, queryCounty, queryMunicipality, querySuburb, queryHash, from]);

    useEffect(() => {
        setFetchingLocation(false);

        if (!position) {
            updateUrl({ hash: '' });
        } else {
            const longitude = position?.coords?.longitude;
            const latitude = position?.coords?.latitude;

            if (longitude && latitude) {
                updateUrl({
                    county: '',
                    municipality: '',
                    suburb: '',
                    query: '',
                    hash: GeoHash.encodeGeoHash(latitude, longitude),
                });
            }
        }
    }, [position]);

    useEffect(() => {
        if (queryCounty) {
            fetchAllMunicipalities({ baseUrl, county: queryCounty }).then((municipalities) =>
                setAllMunicipalities(municipalities)
            );
        } else {
            setAllMunicipalities([]);
        }
    }, [queryCounty]);

    useEffect(() => {
        if (queryMunicipality) {
            fetchAllSuburbs({ baseUrl, municipality: queryMunicipality }).then((suburbs) => setAllSuburbs(suburbs));
        } else {
            setAllSuburbs([]);
        }
    }, [queryMunicipality]);

    useEffect(() => {
        fetchAllCounties({ baseUrl }).then((counties) => setAllCounties(counties));
    }, []);

    const handleCountyClick = (value) => {
        updateUrl({ county: value, municipality: '', suburb: '', hash: '' });
    };

    const handleMunicipalityClick = (value) => {
        updateUrl({ municipality: value, suburb: '', hash: '' });
    };

    const handleSuburbClick = (value) => {
        updateUrl({ suburb: value, hash: '' });
    };

    const search = ({ query, county, municipality, suburb, geohash, from }) => {
        try {
            setSearching(true);
            if (!from || from === 0) {
                dispatch(clearProjectCardsState());
                setResults([]);
            }
            performSearch({
                searchString: query,
                eventIds: [tvaEventId, p3aEventId].filter((pid) => !!pid),
                geohash,
                county,
                municipality,
                suburb,
                baseUrl,
                from,
                max,
                sort: !query || replacedLocation ? 'collected' : null,
            }).then((searchResults) => {
                if (searchResults.projects) {
                    if (from > 0) {
                        setResults(
                            Array.from(new Set([...results, ...(searchResults.projects?.map((p) => p.id) ?? [])]))
                        );
                    } else {
                        setResults(searchResults.projects?.map((p) => p.id));
                    }
                } else {
                    setResults([]);
                }

                setSearching(false);
                setTotal(searchResults.total);

                if (county) {
                    setCurrentCounty(searchResults?.counties?.filter((c) => c.key === county)[0]);
                } else {
                    setCurrentCounty(null);
                }

                if (municipality) {
                    const foundMunicipality = searchResults?.municipalities?.filter(
                        (m) => m.key[0] === municipality
                    )[0];
                    if (foundMunicipality) {
                        setCurrentMunicipality({
                            key: foundMunicipality.key[0],
                            count: foundMunicipality.count,
                            sum: foundMunicipality.sum,
                        });
                    } else {
                        setCurrentMunicipality(null);
                    }
                } else {
                    setCurrentMunicipality(null);
                }

                if (suburb) {
                    setCurrentSuburb(searchResults?.suburbs?.filter((c) => c.key === suburb)[0]);
                } else {
                    setCurrentSuburb(null);
                }
            });
        } catch (e) {}

        fetchPopulation({ county, municipality, baseUrl }).then((result) => {
            let populationFromResult;

            if (result?.municipality?.[municipality] && !suburb) {
                populationFromResult = result?.municipality?.[municipality];
            } else if (result?.county?.[county]) {
                populationFromResult = result.county[county];
            }
            setPopulation(populationFromResult);
        });
    };

    const findCurrentPosition = () => {
        if (navigator && navigator.geolocation) {
            setFetchingLocation(true);
            navigator.geolocation.getCurrentPosition(
                setPosition,
                () => {
                    setPosition(Math.random());
                },
                { enableHighAccuracy: false }
            );
        }
    };

    const currentAreaSelection = currentSuburb ?? currentMunicipality ?? currentCounty;

    useEffect(() => {
        if (fetchMore) {
            setFrom(results.length);
        }
    }, [fetchMore]);

    return (
        <>
            <MaxWidthWrapper>
                <div className={styles.mainContainer}>
                    <div className={styles.searchWrapper}>
                        <form
                            className={[styles.searchContainer, focused ? styles.focused : ''].join(' ')}
                            onSubmit={searchSubmit}
                            action={'.'}
                        >
                            <InputTextLike
                                placeholder={'Søk'}
                                ariaLabel={'Søk'}
                                type="search"
                                name="search"
                                onFocus={() => setFocused(true)}
                                onBlur={() => {
                                    setFocused(false);
                                    if (searchField.current.value === '') {
                                        updateUrl({ query: '' });
                                    }
                                }}
                                ref={searchField}
                                className={styles.bosseSok}
                            />
                            <RoundedButtonPrimary className={styles.searchButton} disabled={searching}>
                                {searching ? 'Søker …' : 'Søk'}
                            </RoundedButtonPrimary>
                        </form>
                        <div className={styles.areaSelections}>
                            <AreaDropdown
                                currentSelected={queryCounty}
                                allRegions={allCounties}
                                onChange={handleCountyClick}
                                label={'Velg fylke'}
                            />
                            {queryCounty &&
                                queryMunicipality !== 'Oslo' &&
                                queryMunicipality !== 'Svalbard' &&
                                queryMunicipality !== 'Jan Mayen' && (
                                    <AreaDropdown
                                        currentSelected={queryMunicipality}
                                        allRegions={allMunicipalities}
                                        onChange={handleMunicipalityClick}
                                        label={'Alle kommuner'}
                                    />
                                )}
                            {queryCounty && queryMunicipality && (
                                <AreaDropdown
                                    currentSelected={querySuburb}
                                    allRegions={allSuburbs}
                                    onChange={handleSuburbClick}
                                    label={'Alle bydeler'}
                                />
                            )}
                        </div>
                        <div className={styles.positionButtons}>
                            {queryHash ? (
                                <ButtonBase className={styles.clearButton} onClick={() => setPosition(null)}>
                                    I nærheten av meg
                                    <span
                                        className={['fa-solid fa-xmark', styles.closeIcon].join(' ')}
                                        title={'Fjern valg'}
                                    />
                                </ButtonBase>
                            ) : (
                                <ButtonText onClick={findCurrentPosition} className={styles.positionButton}>
                                    <i className={['fa-solid fa-location-dot', styles.geoIcon].join(' ')} aria-hidden />{' '}
                                    <span className={styles.geoText}>I nærheten av meg</span>
                                    {fetchingLocation && (
                                        <Spinner
                                            size={'10px'}
                                            borderWidth={'2px'}
                                            color={'black'}
                                            className={styles.spinner}
                                        />
                                    )}
                                </ButtonText>
                            )}
                        </div>
                        {replacedLocation && (
                            <div className={styles.didYouMeanButtonContainer}>
                                Mente du {replacedLocation}
                                <ButtonText
                                    tightFocus
                                    className={styles.didYouMeanButton}
                                    onClick={() =>
                                        updateUrl({
                                            county: '',
                                            municipality: '',
                                            suburb: '',
                                            hash: '',
                                            query: replacedLocation,
                                        })
                                    }
                                >
                                    i hele landet?
                                </ButtonText>
                            </div>
                        )}
                    </div>

                    {queryCounty && (!urlQueryParameter || replacedLocation) && currentAreaSelection?.key && (
                        <AreaInfo
                            numberOfProjects={currentAreaSelection.count}
                            collectedAmount={currentAreaSelection.sum}
                            areaName={currentAreaSelection.key}
                            population={population}
                        />
                    )}

                    <div className={styles.projectCardsContainer}>
                        <ProjectCardContext.Provider
                            value={{
                                supportButton: false,
                                footer: CARD_FOOTER_OPTIONS.location,
                                timeRemaining: false,
                                receiver: false,
                                owner: false,
                                moneygoal: false,
                                collected_amount: true,
                                profilePicture: true,
                                footerLocationInformation:
                                    queryCounty || queryStringParsed
                                        ? [
                                              !queryMunicipality &&
                                                  CARD_FOOTER_LOCATION_INFORMATION_OPTIONS.municipality_name,
                                              !querySuburb && CARD_FOOTER_LOCATION_INFORMATION_OPTIONS.suburb,
                                          ].filter((element) => element)
                                        : CARD_FOOTER_LOCATION_INFORMATION_OPTIONS.county_name,
                            }}
                        >
                            <TvaProjectCards loading={false} projectIds={results} />
                        </ProjectCardContext.Provider>
                        <VisibilitySensor
                            active={from + max < total && !searching}
                            onChange={setFetchMore}
                            offset={{ bottom: 300 }}
                        >
                            {from + max < total && <LoaderBar />}
                        </VisibilitySensor>
                    </div>
                </div>
            </MaxWidthWrapper>
            {!searching && !results?.length && (
                <NoHits query={urlQueryParameter} cmsSelectedProjects={cmsSelectedProjects} county={queryCounty} />
            )}
        </>
    );
};
const mapStateToProps = (state) => {
    const cmsSelectedProjects = state.campaigns.tvaBosseCmsSelectedProjects?.split(',') ?? [];

    return {
        cmsSelectedProjects,
        baseUrl: state.baseURL,
    };
};

export default connect(mapStateToProps)(TvaFindBosse);
